import React, { useState, useEffect } from 'react';
import './Womens.css';
import Footer from '../../components/Footer/Footer';
import CardImageCarousel from '../../components/CardImageCarousel/CardImageCarousel';
import { fetchWomensContent } from '../../services/api';

const Womens = () => {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'KU';
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPublication, setSelectedPublication] = useState(null);
  const [contentData, setContentData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedLang = localStorage.getItem('selectedLanguage') || 'KU';
    setCurrentLang(savedLang);

    const handleLanguageChange = (event) => {
      setCurrentLang(event.detail);
      localStorage.setItem('selectedLanguage', event.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  // Fetch all content from API
  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const allContent = await fetchWomensContent();
        
        // Group content by section_type
        const grouped = {
          about: [],
          activities: [],
          academia: [],
          publishings: [],
          podcasts: [],
          applications: []
        };
        
        allContent.forEach(item => {
          if (grouped[item.section_type]) {
            grouped[item.section_type].push(item);
          }
        });
        
        setContentData(grouped);
      } catch (error) {
        console.error('Failed to load womens content:', error);
        setContentData({});
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedImage(null);
        setSelectedPublication(null);
      }
    };
    if (selectedImage || selectedPublication) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage, selectedPublication]);

  const content = {
    EN: {
      title: 'Women\'s Section',
      subtitle: 'Empowering women through education, activities, and resources',
      about: 'About',
      activities: 'Activities',
      academia: 'Academia',
      publishings: 'Publications',
      podcasts: 'Podcasts',
      applications: 'Applications'
    },
    KU: {
      title: 'بەشی خانمان',
      subtitle: 'بەهێزکردنی خانمان لە ڕێگەی پەروەردە، کاروچالاکی و سەرچاوەکان',
      about: 'دەربارە',
      activities: 'کاروچالاکیەکان',
      academia: 'ئەکادیمیا',
      publishings: 'بڵاوکراوەکان',
      podcasts: 'پۆدکاستەکان',
      applications: 'بەرنامەکان'
    },
    AR: {
      title: 'قسم النساء',
      subtitle: 'تمكين النساء من خلال التعليم والأنشطة والموارد',
      about: 'حول',
      activities: 'الأنشطة',
      academia: 'الأكاديمية',
      publishings: 'المنشورات',
      podcasts: 'البودكاست',
      applications: 'التطبيقات'
    },
    TR: {
      title: 'Kadınlar Bölümü',
      subtitle: 'Eğitim, faaliyetler ve kaynaklar aracılığıyla kadınları güçlendirmek',
      about: 'Hakkında',
      activities: 'Faaliyetler',
      academia: 'Akademi',
      publishings: 'Yayınlar',
      podcasts: 'Podcastler',
      applications: 'Uygulamalar'
    }
  };

  const pageContent = content[currentLang] || content.KU;

  // Render About Section
  const renderAboutSection = () => {
    const aboutItems = contentData.about || [];
    if (aboutItems.length === 0) return null;

    return (
      <section className="womens-section about-section">
        <div className="section-header">
          <h2 className="section-title">{pageContent.about}</h2>
        </div>
        <div className="about-cards">
          {aboutItems.map((item) => {
            const itemContent = item.content?.[currentLang] || item.content?.EN || '';
            const itemTitle = item.title?.[currentLang] || item.title?.EN || '';
            
            return (
              <div key={item.id} className="about-card">
                {itemTitle && <h3 className="about-card-title">{itemTitle}</h3>}
                {itemContent && (
                  <div className="about-card-content">
                    {itemContent.split('\n').map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // Render Activities Section
  const renderActivitiesSection = () => {
    const activities = contentData.activities || [];
    if (activities.length === 0) return null;

    return (
      <section className="womens-section activities-section">
        <div className="section-header">
          <h2 className="section-title">{pageContent.activities}</h2>
        </div>
        <div className="activities-grid">
          {activities.map((activity) => {
            const activityContent = {
              title: activity.title?.[currentLang] || activity.title?.EN || '',
              description: activity.description?.[currentLang] || activity.description?.EN || ''
            };
            const images = (activity.images || []).map(img => 
              typeof img === 'string' ? img : (img.url || img.src || img)
            ).filter(url => url && url.trim() !== '');

            return (
              <div key={activity.id} className="activity-card">
                {images && images.length > 0 && (
                  <div className="activity-images-container">
                    {images.length === 1 ? (
                      <div 
                        className="activity-image-single"
                        onClick={() => setSelectedImage(images[0])}
                      >
                        <img src={images[0]} alt={activityContent.title} className="activity-image" loading="lazy" />
                      </div>
                    ) : (
                      <div className="activity-images-grid">
                        {images.map((img, index) => (
                          <div
                            key={index}
                            className="activity-image-item"
                            onClick={() => setSelectedImage(img)}
                          >
                            <img src={img} alt={`${activityContent.title} ${index + 1}`} className="activity-image" loading="lazy" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="activity-content">
                  <h3 className="activity-title">{activityContent.title}</h3>
                  <p className="activity-description">{activityContent.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // Render Academia Section
  const renderAcademiaSection = () => {
    const academiaItems = contentData.academia || [];
    if (academiaItems.length === 0) return null;

    return (
      <section className="womens-section academia-section">
        <div className="section-header">
          <h2 className="section-title">{pageContent.academia}</h2>
        </div>
        <div className="academia-items-grid">
          {academiaItems.map((item) => {
            const itemContent = {
              title: item.title?.[currentLang] || item.title?.EN || '',
              description: item.description?.[currentLang] || item.description?.EN || ''
            };
            const images = (item.images || []).map(img => 
              typeof img === 'string' ? img : (img.url || img.src || img)
            ).filter(url => url && url.trim() !== '');

            return (
              <div key={item.id} className="academia-item-card">
                {images && images.length > 0 && (
                  <div className="academia-item-images-container">
                    <CardImageCarousel images={images} altText={itemContent.title} />
                  </div>
                )}
                <div className="academia-item-content">
                  <h3 className="academia-item-title">{itemContent.title}</h3>
                  <p className="academia-item-description">{itemContent.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // Render Publishings Section
  const renderPublishingsSection = () => {
    const publishings = contentData.publishings || [];
    if (publishings.length === 0) return null;

    return (
      <section className="womens-section publishings-section">
        <div className="section-header">
          <h2 className="section-title">{pageContent.publishings}</h2>
        </div>
        <div className="publications-grid">
          {publishings.map((publication) => {
            const pubContent = {
              title: publication.title?.[currentLang] || publication.title?.EN || '',
              description: publication.description?.[currentLang] || publication.description?.EN || ''
            };
            const image = publication.image || '';

            return (
              <div 
                key={publication.id} 
                className="publication-card"
                onClick={() => setSelectedPublication({ ...pubContent, image })}
              >
                <div className="publication-image-wrapper">
                  {image ? (
                    <img src={image} alt={pubContent.title} className="publication-image" loading="lazy" />
                  ) : (
                    <div className="publication-image-placeholder">
                      <span>No Image</span>
                    </div>
                  )}
                  <div className="publication-overlay"></div>
                </div>
                <div className="publication-content">
                  <h3 className="publication-title">{pubContent.title}</h3>
                  <p className="publication-description">{pubContent.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // Render Podcasts Section
  const renderPodcastsSection = () => {
    const podcasts = contentData.podcasts || [];
    if (podcasts.length === 0) return null;

    return (
      <section className="womens-section podcasts-section">
        <div className="section-header">
          <h2 className="section-title">{pageContent.podcasts}</h2>
        </div>
        <div className="podcasts-grid">
          {podcasts.map((podcast) => {
            const podcastContent = {
              title: podcast.title?.[currentLang] || podcast.title?.EN || '',
              description: podcast.description?.[currentLang] || podcast.description?.EN || '',
              link: podcast.link || '',
              linkText: podcast.linkText?.[currentLang] || podcast.linkText?.EN || 'Listen Now'
            };
            const image = podcast.image || '';

            return (
              <div key={podcast.id} className="podcast-card">
                <div className="podcast-image-container">
                  {image ? (
                    <img src={image} alt={podcastContent.title} className="podcast-image" loading="lazy" />
                  ) : (
                    <div className="podcast-image-placeholder">
                      <div className="play-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                  )}
                  <div className="podcast-image-overlay">
                    <div className="play-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="podcast-content">
                  <h3 className="podcast-title">{podcastContent.title}</h3>
                  <p className="podcast-description">{podcastContent.description}</p>
                  {podcastContent.link && (
                    <a
                      href={podcastContent.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="podcast-link-button"
                    >
                      <span>{podcastContent.linkText}</span>
                      <svg className="link-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // Render Applications Section
  const renderApplicationsSection = () => {
    const applications = contentData.applications || [];
    if (applications.length === 0) return null;

    return (
      <section className="womens-section applications-section">
        <div className="section-header">
          <h2 className="section-title">{pageContent.applications}</h2>
        </div>
        <div className="apps-grid">
          {applications.map((app) => {
            const appContent = {
              name: app.title?.[currentLang] || app.title?.EN || '',
              description: app.description?.[currentLang] || app.description?.EN || '',
              appStoreLink: app.appStoreLink || '',
              playStoreLink: app.playStoreLink || ''
            };
            const image = app.image || '';

            return (
              <div key={app.id} className="app-card">
                <div className="app-image-container">
                  {image ? (
                    <img src={image} alt={appContent.name} className="app-image" loading="lazy" />
                  ) : (
                    <div className="app-image-placeholder">
                      <span>No Image</span>
                    </div>
                  )}
                  <div className="app-image-overlay"></div>
                </div>
                <div className="app-content">
                  <h3 className="app-name">{appContent.name}</h3>
                  <p className="app-description">{appContent.description}</p>
                  <div className="app-downloads">
                    {appContent.appStoreLink && (
                      <a
                        href={appContent.appStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="download-button app-store-button"
                      >
                        <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.96-3.24-.96-1.23 0-2.08.5-3.26.99-1.31.58-2.36.66-3.4.4A4.46 4.46 0 0 1 2.03 16.9c-1.18-2.03-.96-4.73.8-6.47 1.12-1.3 2.7-2.1 4.2-2.1 1.23 0 2.25.46 3.03.9.7.38 1.3.7 1.87.7.5 0 1.1-.3 1.87-.7.85-.46 1.9-.9 3.1-.9 1.35 0 2.85.7 3.9 1.8-3.4 1.9-2.85 5.8-.6 7.2-.7.4-1.1.9-1.1 1.6 0 1.3 1.2 2.4 2.7 2.4.6 0 1.2-.2 1.7-.5.6-.3 1.1-.6 1.9-.6.8 0 1.3.3 1.9.6.5.3 1.1.5 1.7.5 1.5 0 2.7-1.1 2.7-2.4 0-.7-.4-1.2-1.1-1.6zM14.7 4.7c.7-.8 1.2-2 1-3.2-1 .1-2.2.7-2.9 1.5-.7.8-1.2 2-1 3.1 1.1.1 2.2-.5 2.9-1.4z"/>
                        </svg>
                        <div className="button-text">
                          <span className="button-label">Download on</span>
                          <span className="button-store">App Store</span>
                        </div>
                      </a>
                    )}
                    {appContent.playStoreLink && (
                      <a
                        href={appContent.playStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="download-button play-store-button"
                      >
                        <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                        </svg>
                        <div className="button-text">
                          <span className="button-label">Download on</span>
                          <span className="button-store">Play Store</span>
                        </div>
                      </a>
                    )}
                    {!appContent.appStoreLink && !appContent.playStoreLink && (
                      <div className="coming-soon-badge">Coming Soon</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <div className="womens-page">
      <div className="container">
        <div className="womens-content">
          {/* Title Section */}
          <div className="womens-title-section">
            <h1 className="page-title">{pageContent.title}</h1>
            <p className="page-subtitle">{pageContent.subtitle}</p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading content...</p>
            </div>
          )}

          {/* All Sections */}
          {!loading && (
            <>
              {renderAboutSection()}
              {renderActivitiesSection()}
              {renderAcademiaSection()}
              {renderPublishingsSection()}
              {renderPodcastsSection()}
              {renderApplicationsSection()}
            </>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="image-modal-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close"
            >
              ×
            </button>
            <img src={selectedImage} alt="Enlarged view" />
          </div>
        </div>
      )}

      {/* Publication Detail Modal */}
      {selectedPublication && (
        <div className="publication-modal" onClick={() => setSelectedPublication(null)}>
          <div className="publication-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="publication-modal-close"
              onClick={() => setSelectedPublication(null)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="publication-modal-image-wrapper">
              <img src={selectedPublication.image} alt={selectedPublication.title} className="publication-modal-image" />
            </div>
            <div className="publication-modal-info">
              <h2 className="publication-modal-title">{selectedPublication.title}</h2>
              <p className="publication-modal-description">{selectedPublication.description}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Womens;

